import React, { useState, useEffect, useCallback } from 'react';

import { FlexColumn, FlexRow } from '@editor/components/utilities/layouts';
import { Slider as SliderNew } from '@repo/ui-kit/components/core/Slider';
import { H7 } from '@repo/ui-kit/components/core/Typography';

import { VanillaNumberInput as NumberInput } from '../NumberInput/NumberInput';
import Slider from '../Slider/Slider';

import { Container, SliderContainer } from './styles';
import { getDefaultSliderInputTheme } from './theme';
import { SliderInputProps } from './types';

const SliderInput: React.FC<SliderInputProps> = ({
  min = 0,
  max = 100,
  precision = 2,
  pointerPolicy = 'delta',
  hasNumberInput = true,
  startValue,
  onChanged,
  onChanging,
  useLegacySlider,
  ...props
}) => {
  const [value, setValue] = useState(startValue);
  const [lastInputValue, setLastInputValue] = useState(value);

  useEffect(() => {
    setValue(startValue);
  }, [startValue]);

  const _onChanged = useCallback(
    (value: number) => {
      setValue(value);
      if (onChanged) {
        onChanged(value);
      }
    },
    [onChanged]
  );

  const _onChanging = useCallback(
    (value: number) => {
      setValue(value);
      if (onChanging) {
        onChanging(value);
      }
    },
    [onChanging]
  );

  const theme = {
    ...getDefaultSliderInputTheme(),
    ...props.theme,
  };

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>): void => {
      (event.target as HTMLDivElement).setPointerCapture(event.pointerId);
    },
    []
  );

  const { allowWheel = 'all' } = props;

  const onBlur = (value: number): void => {
    setLastInputValue(value);
  };

  const sliderProps = {
    ...props,
    max: Math.max(props.sliderMax ?? max, lastInputValue),
    min,
    precision,
  };
  delete sliderProps.dataTestId;
  delete sliderProps.allowWheel;
  delete sliderProps.showCenterGuide;

  if (!useLegacySlider) {
    return (
      <FlexColumn data-testid={props.dataTestId} width="100%">
        <FlexRow
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <H7 color={props.disabled ? 'disabled' : undefined}>{props.label}</H7>
          {hasNumberInput && (
            <NumberInput
              selectOnFocus
              {...props}
              dataTestId={
                props.dataTestId ? `${props.dataTestId}--number-input` : ''
              }
              max={max}
              min={min}
              precision={precision}
              theme={{
                ...theme,
                width: '43px',
                height: '20px',
                borderColor: 'transparent',
              }}
              value={{ value }}
              onChanged={_onChanged}
              onChanging={_onChanging}
              onEnter={onBlur}
            />
          )}
        </FlexRow>
        <FlexRow
          data-testid={props.dataTestId ? `${props.dataTestId}--slider` : ''}
          width="100%"
        >
          <SliderNew
            {...sliderProps}
            value={[value]}
            onValueChange={(value) => _onChanging(value[0])}
            onValueCommit={(value) => _onChanged(value[0])}
          />
        </FlexRow>
      </FlexColumn>
    );
  }

  return (
    <Container
      data-testid={props.dataTestId}
      disabled={props.disabled}
      theme={theme}
      onPointerDown={onPointerDown}
    >
      <SliderContainer
        hasNumberInput={hasNumberInput}
        numberInputWidth={theme.width}
      >
        <Slider
          allowWheel={allowWheel}
          {...sliderProps}
          dataTestId={props.dataTestId ? `${props.dataTestId}--slider` : ''}
          pointerPolicy={pointerPolicy}
          showCenterGuide={props.showCenterGuide}
          theme={theme}
          value={value}
          onChanged={_onChanged}
          onChanging={_onChanging}
        />
      </SliderContainer>
      {hasNumberInput && (
        <NumberInput
          selectOnFocus
          {...props}
          dataTestId={
            props.dataTestId ? `${props.dataTestId}--number-input` : ''
          }
          max={max}
          min={min}
          precision={precision}
          theme={{
            ...theme,
            marginBottom: props.marks || props.showCenterGuide ? '10px' : '0',
          }}
          value={{ value }}
          onChanged={_onChanged}
          onChanging={_onChanging}
          onEnter={onBlur}
        />
      )}
    </Container>
  );
};

export default SliderInput;
